import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { clientCompanyApi } from '@/api/clientCompany'
import { useCompany } from '@/hooks/useCompany'

/**
 * The company whose Website these screens check domains against, loaded into the company context. CompanyProvider
 * never loads it (only Company settings and the console do), so without this a page opened directly reads no Website.
 */
export function useLoadedCompany(clientCompanyId: string) {
  const { company, updateCompany } = useCompany()
  const [failure, setFailure] = useState<{ clientCompanyId: string; message: string } | null>(null)
  // The provider hands out a new updateCompany whenever its state changes, which would restart a load in flight.
  const updateCompanyRef = useRef(updateCompany)
  updateCompanyRef.current = updateCompany
  // Only the latest load may write, so a reload that overtakes an earlier one is not undone by it.
  const requestIdRef = useRef(0)
  const loaded = company?.Id === clientCompanyId ? company : null

  const reload = useCallback(async () => {
    const requestId = ++requestIdRef.current
    setFailure(null)
    const res = await clientCompanyApi.getById(clientCompanyId)
    if (requestIdRef.current !== requestId) return
    if (res.data && !res.error) updateCompanyRef.current(res.data)
    else setFailure({ clientCompanyId, message: res.error || 'The company could not be loaded.' })
  }, [clientCompanyId])

  // A load still running for another company, or once the page has closed, must not write.
  useEffect(() => () => { requestIdRef.current++ }, [clientCompanyId])

  // The provider clears the company when it loads a route's company, so this can run again after the first load.
  const isLoaded = loaded !== null
  useEffect(() => {
    if (!isLoaded) void reload()
  }, [isLoaded, reload])

  // An error only says why there is no company: a failed reload keeps the one already loaded.
  const error = !loaded && failure?.clientCompanyId === clientCompanyId ? failure.message : null

  return { company: loaded, error, reload }
}
